import React from 'react';
import { motion } from 'framer-motion';
import { 
  Truck, 
  TrendingUp, 
  TrendingDown, 
  Star, 
  MapPin, 
  Award,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

interface TransportAnalysisProps {
  data: {
    carrier_efficiency: {
      most_efficient: {
        name: string;
        efficiency_score: number;
        on_time_delivery: number;
        cost_per_km: number;
      };
      least_efficient: {
        name: string;
        efficiency_score: number;
        on_time_delivery: number;
        cost_per_km: number;
      };
    };
    freight_costs: {
      most_expensive: {
        name: string;
        avg_cost_per_km: number;
        total_cost: number;
        volume: number;
      };
      cheapest: {
        name: string;
        avg_cost_per_km: number;
        total_cost: number;
        volume: number;
      };
      best_option: {
        name: string;
        cost_efficiency_ratio: number;
        reliability_score: number;
        recommendation: string;
      };
    };
    regional_analysis: {
      best_region: {
        name: string;
        efficiency_score: number;
        avg_cost_per_km: number;
        delivery_time: number;
        volume: number;
      };
      most_expensive_region: {
        name: string;
        avg_cost_per_km: number;
        delivery_time: number;
        volume: number;
        reason: string;
      };
      regional_breakdown: Array<{
        region: string;
        cost_per_km: number;
        volume: number;
        efficiency: number;
      }>;
    };
    carrier_comparison: Array<{
      name: string;
      efficiency: number;
      cost_per_km: number;
      on_time: number;
      volume: number;
      rating: number;
    }>;
  };
}

const TransportAnalysisSection: React.FC<TransportAnalysisProps> = ({ data }) => {
  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center"
      >
        <h2 
          className="text-3xl font-bold mb-4 flex items-center justify-center"
          style={{ color: 'var(--text-primary)' }}
        >
          <Truck 
            className="h-8 w-8 mr-3" 
            style={{ color: 'var(--primary)' }}
          />
          Análise de Transportadoras
        </h2>
        <p 
          className="text-lg"
          style={{ color: 'var(--text-secondary)' }}
        >
          Insights estratégicos sobre eficiência, custos e performance regional
        </p>
      </motion.div>

      {/* Top Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Mais Eficiente */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="rounded-xl p-6 border"
          style={{
            backgroundColor: 'var(--bg-card)',
            borderColor: 'var(--border-primary)',
            boxShadow: 'var(--shadow-lg)'
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-green-500 rounded-lg">
              <Award className="h-6 w-6 text-white" />
            </div>
            <span 
              className="text-sm font-medium"
              style={{ color: 'var(--text-secondary)' }}
            >
              Mais Eficiente
            </span>
          </div>
          <h3 
            className="text-lg font-semibold mb-2"
            style={{ color: 'var(--text-primary)' }}
          >
            {data?.carrier_efficiency?.most_efficient?.name || 'N/A'}
          </h3>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="style={{ color: 'var(--text-secondary)' }}">Eficiência:</span>
              <span 
                className="font-semibold"
                style={{ color: 'var(--text-primary)' }}
              >
                {data?.carrier_efficiency?.most_efficient?.efficiency_score || 0}%
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="style={{ color: 'var(--text-secondary)' }}">Pontualidade:</span>
              <span 
                className="font-semibold"
                style={{ color: 'var(--text-primary)' }}
              >
                {data?.carrier_efficiency?.most_efficient?.on_time_delivery || 0}%
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="style={{ color: 'var(--text-secondary)' }}">Custo/km:</span>
              <span 
                className="font-semibold"
                style={{ color: 'var(--text-primary)' }}
              >
                R$ {data?.carrier_efficiency?.most_efficient?.cost_per_km.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) || 0}
              </span>
            </div>
          </div>
        </motion.div>

        {/* Mais Cara */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="rounded-xl p-6 border relative overflow-hidden"
          style={{
            backgroundColor: 'var(--bg-card)',
            borderColor: 'var(--border-primary)',
            boxShadow: 'var(--shadow-md)'
          }}
        >
          <div className="relative flex items-center justify-between mb-4">
            <div className="p-3 rounded-lg bg-gradient-to-br from-red-500 to-red-600">
              <TrendingUp className="h-6 w-6 text-white" />
            </div>
            <span 
              className="text-sm font-medium"
              style={{ color: 'var(--text-secondary)' }}
            >
              Mais Cara
            </span>
          </div>
          <h3 
            className="text-lg font-semibold mb-2"
            style={{ color: 'var(--text-primary)' }}
          >
            {data?.freight_costs?.most_expensive?.name || 'N/A'}
          </h3>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span 
                style={{ color: 'var(--text-secondary)' }}
              >
                Custo/km:
              </span>
              <span 
                className="font-semibold"
                style={{ color: 'var(--text-primary)' }}
              >
                R$ {data?.freight_costs?.most_expensive?.avg_cost_per_km.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) || 0}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span 
                style={{ color: 'var(--text-secondary)' }}
              >
                Volume:
              </span>
              <span 
                className="font-semibold"
                style={{ color: 'var(--text-primary)' }}
              >
                {(data?.freight_costs?.most_expensive?.volume || 0).toLocaleString()} km
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span 
                style={{ color: 'var(--text-secondary)' }}
              >
                Total:
              </span>
              <span 
                className="font-semibold"
                style={{ color: 'var(--text-primary)' }}
              >
                R$ {(data?.freight_costs?.most_expensive?.total_cost.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) || 0).toLocaleString()}
              </span>
            </div>
          </div>
        </motion.div>

        {/* Mais Barata */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3 }}
          className="rounded-xl p-6 border relative overflow-hidden"
          style={{
            backgroundColor: 'var(--bg-card)',
            borderColor: 'var(--border-primary)',
            boxShadow: 'var(--shadow-md)'
          }}
        >
          <div className="relative flex items-center justify-between mb-4">
            <div className="p-3 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600">
              <TrendingDown className="h-6 w-6 text-white" />
            </div>
            <span 
              className="text-sm font-medium"
              style={{ color: 'var(--text-secondary)' }}
            >
              Mais Barata
            </span>
          </div>
          <h3 
            className="text-lg font-semibold mb-2"
            style={{ color: 'var(--text-primary)' }}
          >
            {data?.freight_costs?.cheapest?.name || 'N/A'}
          </h3>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span 
                style={{ color: 'var(--text-secondary)' }}
              >
                Custo/km:
              </span>
              <span 
                className="font-semibold"
                style={{ color: 'var(--text-primary)' }}
              >
                R$ {data?.freight_costs?.cheapest?.avg_cost_per_km.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) || 0}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span 
                style={{ color: 'var(--text-secondary)' }}
              >
                Volume:
              </span>
              <span 
                className="font-semibold"
                style={{ color: 'var(--text-primary)' }}
              >
                {(data?.freight_costs?.cheapest?.volume || 0).toLocaleString()} km
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span 
                style={{ color: 'var(--text-secondary)' }}
              >
                Total:
              </span>
              <span 
                className="font-semibold"
                style={{ color: 'var(--text-primary)' }}
              >
                R$ {(data?.freight_costs?.cheapest?.total_cost.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) || 0).toLocaleString()}
              </span>
            </div>
          </div>
        </motion.div>

        {/* Melhor Opção */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4 }}
          className="rounded-xl p-6 border relative overflow-hidden"
          style={{
            backgroundColor: 'var(--bg-card)',
            borderColor: 'var(--border-primary)',
            boxShadow: 'var(--shadow-md)'
          }}
        >
          <div className="relative flex items-center justify-between mb-4">
            <div className="p-3 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600">
              <Star className="h-6 w-6 text-white" />
            </div>
            <span 
              className="text-sm font-medium"
              style={{ color: 'var(--text-secondary)' }}
            >
              Melhor Opção
            </span>
          </div>
          <h3 
            className="text-lg font-semibold mb-2"
            style={{ color: 'var(--text-primary)' }}
          >
            {data?.freight_costs?.best_option?.name || 'N/A'}
          </h3>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span 
                style={{ color: 'var(--text-secondary)' }}
              >
                Custo-Benefício:
              </span>
              <span 
                className="font-semibold"
                style={{ color: 'var(--text-primary)' }}
              >
                {data?.freight_costs?.best_option?.cost_efficiency_ratio.toFixed(2) || '0.00'}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span 
                style={{ color: 'var(--text-secondary)' }}
              >
                Confiabilidade:
              </span>
              <span 
                className="font-semibold"
                style={{ color: 'var(--text-primary)' }}
              >
                {data?.freight_costs?.best_option?.reliability_score.toFixed(2) || '0.00'}%
              </span>
            </div>
            <div 
              className="text-xs font-medium"
              style={{ color: 'var(--text-tertiary)' }}
            >
              {data?.freight_costs?.best_option?.recommendation || 'Recomendação não disponível'}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Comparação de Transportadoras */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="rounded-xl p-6 shadow-lg border"
          style={{
            backgroundColor: 'var(--bg-card)',
            borderColor: 'var(--border-primary)'
          }}
        >
          <h3 
            className="text-xl font-semibold mb-6 flex items-center"
            style={{ color: 'var(--text-primary)' }}
          >
            <BarChart 
              className="h-5 w-5 mr-2" 
              style={{ color: 'var(--primary)' }}
            />
            Comparação de Transportadoras
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.carrier_comparison}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="name" 
                tick={{ fontSize: 12 }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'var(--bg-card)',
                  border: '1px solid var(--border-primary)',
                  borderRadius: '8px'
                }}
              />
              <Bar dataKey="efficiency" fill="#3b82f6" name="Eficiência (%)" />
              <Bar dataKey="on_time" fill="#10b981" name="Pontualidade (%)" />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Análise Regional */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="rounded-xl p-6 shadow-lg border"
          style={{
            backgroundColor: 'var(--bg-card)',
            borderColor: 'var(--border-primary)'
          }}
        >
          <h3 
            className="text-xl font-semibold mb-6 flex items-center"
            style={{ color: 'var(--text-primary)' }}
          >
            <MapPin 
              className="h-5 w-5 mr-2" 
              style={{ color: 'var(--primary)' }}
            />
            Análise Regional
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={data?.regional_analysis?.regional_breakdown || []}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ region, percentage }) => `${region}: ${percentage?.toFixed(1)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="volume"
              >
                {(data?.regional_analysis?.regional_breakdown || []).map((_, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'var(--bg-card)',
                  border: '1px solid var(--border-primary)',
                  borderRadius: '8px'
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </motion.div>
      </div>

      {/* Regional Analysis Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Melhor Região */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.7 }}
          className="rounded-xl p-6 border"
          style={{
            backgroundColor: 'var(--bg-card)',
            borderColor: 'var(--border-primary)',
            boxShadow: 'var(--shadow-lg)'
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-green-500 rounded-lg">
              <CheckCircle className="h-6 w-6 text-white" />
            </div>
            <span 
              className="text-sm font-medium"
              style={{ color: 'var(--text-secondary)' }}
            >
              Melhor Região
            </span>
          </div>
          <h3 
            className="text-xl font-semibold mb-4"
            style={{ color: 'var(--text-primary)' }}
          >
            {data?.regional_analysis?.best_region?.name || 'N/A'}
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center">
              <div 
                className="text-2xl font-bold"
                style={{ color: 'var(--text-primary)' }}
              >
                {data?.regional_analysis?.best_region?.efficiency_score.toFixed(2) || '0.00'}%
              </div>
              <div className="text-sm style={{ color: 'var(--text-secondary)' }}">Eficiência</div>
            </div>
            <div className="text-center">
              <div 
                className="text-2xl font-bold"
                style={{ color: 'var(--text-primary)' }}
              >
                R$ {data?.regional_analysis?.best_region?.avg_cost_per_km.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) || 0.00}
              </div>
              <div className="text-sm style={{ color: 'var(--text-secondary)' }}">Custo/km</div>
            </div>
            <div className="text-center">
              <div 
                className="text-2xl font-bold"
                style={{ color: 'var(--text-primary)' }}
              >
                {data?.regional_analysis?.best_region?.delivery_time || 0}h
              </div>
              <div 
                className="text-sm"
                style={{ color: 'var(--text-secondary)' }}
              >
                Tempo Entrega
              </div>
            </div>
            <div className="text-center">
              <div 
                className="text-2xl font-bold"
                style={{ color: 'var(--text-primary)' }}
              >
                {(data?.regional_analysis?.best_region?.volume || 0).toLocaleString()}
              </div>
              <div 
                className="text-sm"
                style={{ color: 'var(--text-secondary)' }}
              >
                Volume (km)
              </div>
            </div>
          </div>
        </motion.div>

        {/* Região Mais Cara */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.8 }}
          className="rounded-xl p-6 border"
          style={{
            backgroundColor: 'var(--bg-card)',
            borderColor: 'var(--border-primary)',
            boxShadow: 'var(--shadow-lg)'
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-red-500 rounded-lg">
              <AlertTriangle className="h-6 w-6 text-white" />
            </div>
            <span 
              className="text-sm font-medium"
              style={{ color: 'var(--text-secondary)' }}
            >
              Região Mais Cara
            </span>
          </div>
          <h3 
            className="text-xl font-semibold mb-4"
            style={{ color: 'var(--text-primary)' }}
          >
            {data?.regional_analysis?.most_expensive_region?.name || 'N/A'}
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center">
              <div 
                className="text-2xl font-bold"
                style={{ color: 'var(--text-primary)' }}
              >
                R$ {data?.regional_analysis?.most_expensive_region?.avg_cost_per_km.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) || 0.00}
              </div>
              <div className="text-sm style={{ color: 'var(--text-secondary)' }}">Custo/km</div>
            </div>
            <div className="text-center">
              <div 
                className="text-2xl font-bold"
                style={{ color: 'var(--text-primary)' }}
              >
                  {data?.regional_analysis?.most_expensive_region?.delivery_time || 0}h
              </div>
              <div 
                className="text-sm"
                style={{ color: 'var(--text-secondary)' }}
              >
                Tempo Entrega
              </div>
            </div>
            <div className="text-center">
              <div 
                className="text-2xl font-bold"
                style={{ color: 'var(--text-primary)' }}
              >
                {(data?.regional_analysis?.most_expensive_region?.volume || 0).toLocaleString()}
              </div>
              <div 
                className="text-sm"
                style={{ color: 'var(--text-secondary)' }}
              >
                Volume (km)
              </div>
            </div>
            <div className="text-center">
              <div 
                className="text-xs font-medium"
                style={{ color: 'var(--text-secondary)' }}
              >
                {data?.regional_analysis?.most_expensive_region?.reason || 'N/A'}
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Carrier Performance Horizontal Bar Chart */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9 }}
        className="rounded-xl p-6 shadow-lg border"
          style={{
            backgroundColor: 'var(--bg-card)',
            borderColor: 'var(--border-primary)'
          }}
      >
        <h3 
          className="text-xl font-semibold mb-6 flex items-center"
          style={{ color: 'var(--text-primary)' }}
        >
          <BarChart 
            className="h-5 w-5 mr-2" 
            style={{ color: 'var(--primary)' }}
          />
          Comparação de Performance por Transportadora
        </h3>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart 
            data={data.carrier_comparison}
            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis 
              dataKey="name" 
              tick={{ fontSize: 12 }}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis 
              tick={{ fontSize: 12 }}
            />
            <Tooltip 
              contentStyle={{
                backgroundColor: 'var(--bg-card)',
                border: '1px solid var(--border-primary)',
                borderRadius: '8px'
              }}
            />
            <Bar 
              dataKey="efficiency" 
              fill="#3b82f6" 
              name="Eficiência (%)"
              radius={[4, 4, 0, 0]}
            />
            <Bar 
              dataKey="on_time" 
              fill="#10b981" 
              name="Pontualidade (%)"
              radius={[4, 4, 0, 0]}
            />
            <Bar 
              dataKey="rating" 
              fill="#f59e0b" 
              name="Rating (1-5)"
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
        <div className="mt-4 flex justify-center space-x-6">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-blue-500 rounded"></div>
            <span 
              className="text-sm"
              style={{ color: 'var(--text-secondary)' }}
            >
              Eficiência (%)
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-green-500 rounded"></div>
            <span 
              className="text-sm"
              style={{ color: 'var(--text-secondary)' }}
            >
              Pontualidade (%)
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-yellow-500 rounded"></div>
            <span 
              className="text-sm"
              style={{ color: 'var(--text-secondary)' }}
            >
              Rating (1-5)
            </span>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default TransportAnalysisSection;
